import { useState, useEffect, useCallback } from "react";
import { getFinancialSystems } from "../services/onboardingApiService";

const BLACKLISTED_FINANCIAL_SYSTEMS = ["Tripltex", "Annet", "ukjent"];

/**
 * Some of the financial systems are duplicates / erroneous entries, and I do not have access to the system to clean it up.
 * @param {*} financialSystems
 * @returns
 */
function removeBlacklistedFinancialSystems(financialSystems) {
  return financialSystems.filter((system) => {
    return !BLACKLISTED_FINANCIAL_SYSTEMS.some(
      (blacklisted) => blacklisted.toLowerCase() === system.label.toLowerCase()
    );
  });
}

// Financial system helper URLs mapping
const FINANCIAL_SYSTEM_HELP_URLS = {
  Tripletex: "https://kred.no/okonomisystem/tripletex",
  // Future systems can be added here:
  // "Visma Business": "https://kred.no/okonomisystem/visma-business",
  // "PowerOffice": "https://kred.no/okonomisystem/poweroffice",
};

// External financial systems that require initiation from the system itself
const EXTERNAL_FINANCIAL_SYSTEMS = {
  Poweroffice: "https://kred.no/okonomisystem/power-office-go/",
  Unimicro: "https://kred.no/okonomisystem/uni-micro/",
  "24/7 Office": "https://kred.no/okonomisystem/24sevenoffice/",
};

// Get helper URL for the current selected system
const getHelperUrl = (selectedSystemDisplayName, selectedFinancialSystem) => {
  if (selectedFinancialSystem === null) return null; // No helper for "Annet"
  return FINANCIAL_SYSTEM_HELP_URLS[selectedSystemDisplayName] || null;
};

// Check if a financial system requires external initiation
const isExternalSystem = (selectedFinancialSystem, systemDisplayName) => {
  if (selectedFinancialSystem === null || selectedFinancialSystem === 0)
    return false;
  const isExternal =
    EXTERNAL_FINANCIAL_SYSTEMS.hasOwnProperty(systemDisplayName);
  return isExternal;
};

// Get external URL for financial system
const getExternalUrl = (systemDisplayName) => {
  return EXTERNAL_FINANCIAL_SYSTEMS[systemDisplayName] || null;
};

let financialSystemsCache = [];
let fetchPromise = null;

export const useFinancialSystems = () => {
  const [financialSystems, setFinancialSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFinancialSystems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (financialSystemsCache.length > 0) {
        setFinancialSystems(financialSystemsCache);
        setLoading(false);
        return;
      }

      // Deduplicate requests - reuse existing Promise if fetch is in progress
      if (!fetchPromise) {
        fetchPromise = getFinancialSystems()
          .then(response => {
            // Parse response - API returns JSON string according to documentation
            const systemsData =
              typeof response.data === "string"
                ? JSON.parse(response.data)
                : response.data;

            // Map API response to dropdown format: systemId -> value, systemDescr -> label
            let mappedSystems = Array.isArray(systemsData)
              ? systemsData.map((system) => ({
                  value: system.systemId,
                  label: system.systemDescr,
                }))
              : [];

            mappedSystems = removeBlacklistedFinancialSystems(mappedSystems)
              .sort((a, b) => a.label.localeCompare(b.label, 'nb-NO'))
              .concat([{ value: "null", label: "Annet" }]);

            // Add default "Velg" option at the beginning
            const systemsWithDefault = [
              { value: 0, label: "Velg" },
              ...mappedSystems,
            ];

            financialSystemsCache = systemsWithDefault;
            return systemsWithDefault;
          })
          .catch(error => {
            // Re-throw error to propagate to all consumers awaiting this Promise
            throw error;
          })
          .finally(() => {
            fetchPromise = null; // Reset for future calls
          });
      }

      const systems = await fetchPromise;
      setFinancialSystems(systems);
    } catch (err) {
      console.error("Error fetching financial systems:", err);
      setError("Kunne ikke laste økonomisystemer. Prøv igjen senere.");
      setFinancialSystems([{ value: 0, label: "Velg" }]); // Fallback with default option
    } finally {
      setLoading(false);
    }
  }, []);

  const getSystemDisplayName = useCallback(
    (systemId) => {
      if (systemId === null) return "Annet";
      return (
        financialSystems.find(
          (system) => system.value.toString() === systemId.toString(),
        )?.label || systemId
      );
    },
    [financialSystems],
  );

  const getSystemKey = useCallback(
    (systemDisplayName) => {
      return (
        financialSystems.find((system) => system.label === systemDisplayName)
          ?.value || systemDisplayName
      );
    },
    [financialSystems],
  );

  useEffect(() => {
    fetchFinancialSystems();
  }, [fetchFinancialSystems]);

  return {
    financialSystems,
    loading,
    error,
    refetch: fetchFinancialSystems,
    getHelperUrl,
    getSystemDisplayName,
    getSystemKey,
    isExternalSystem,
    getExternalUrl,
  };
};
